import {
    Grid,
    SelectChangeEvent,
    Theme,
    Typography
} from "@mui/material";
import { MFTextField } from "../../../../lib/formik";
import { TextDatePicker } from "../../../onboarding/DatePickerWithTextFeild";
import MFSelectField from "../../../../lib/formik/SelectField";
import { useSelector } from "react-redux";
import { RootStateType } from "../../../../redux-store/reducers";
import {
    FormikContextType,
    useFormikContext
} from "formik";
import {
    allowDecimalNumbers,
    formatCurrency,
} from "../../../../utils/utilityFunctions";
import dayjs from "dayjs";
import { CurrencyConversionToWords } from "../commonComponents";
import FormikSearchDropdown from "../../../../lib/formik/FormikSearchDropdown";
import * as yup from 'yup';

export default function PmsForm({
    securitiesData,
    fetchSecuritiesData,
    setValidations,
    isLoading
}: {
    securitiesData: Record<string, any>;
    fetchSecuritiesData: (searchInput: string) => void;
    setValidations: (values: Record<string, any>) => void;
    isLoading: boolean;
}): JSX.Element {
    const { currencySymbol, locale = "en-IN", currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);
    const { businessDate = "" } = useSelector((store: RootStateType) => store.navbarInfo);

    const {
        values,
        setFieldValue,
        resetForm
    }: FormikContextType<Record<string, any>> = useFormikContext();

    const handleDateChange = (fieldName: string, selectedDate: any) => {
        setFieldValue(fieldName, selectedDate?.isValid() ? selectedDate : "", true)
    }

    const handleAmountPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
        event.preventDefault()
        const clipboardData = event.clipboardData.getData("text")

        const modifiedText = `${clipboardData ?? ""}`?.replaceAll(",", "")

        setFieldValue("amount", modifiedText);
    }

    const handleSecurityChange = (selectedObj: Record<string, any>) => {
        const marketPrice = !Number.isNaN(Number(selectedObj?.marketPrice ?? 0)) ? Number(selectedObj?.marketPrice ?? 0) : 0

        const qtySaleable = !Number.isNaN(Number(selectedObj?.qtySaleable ?? 0)) ? Number(selectedObj?.qtySaleable ?? 0) : 0

        // Formula to calculate current portfolio value => marketPrice * qtySaleable
        const calculatedPortfolioValue = (marketPrice * qtySaleable)?.toLocaleString(locale)

        if (values?.transactionType === "Withdrawal") {
            setValidations((prevState: Record<string, any>) => ({
                ...prevState,
                amount: yup
                    .string()
                    .transform((value) => `${value ?? ""}`?.replaceAll(',', ''))
                    .required('Amount is required')
                    .test('is-number', 'Amount should be a valid number', function (value) { return !Number.isNaN(Number(value)) })
                    .test('greater-than-zero', "Amount should be greater than 0", function (value) {
                        return Number(value) > 0
                    })
                // .test('whole-number', 'Amount should be a valid number', function (value) {
                //     return numberRegex.test(`${value ?? ""}`);
                // })
                // .test('transaction-type-withdrawal', `Amount should be less than or equal to ${formatCurrency({ value: Number(`${calculatedPortfolioValue ?? ""}`?.replaceAll(',', '')), locale, currencyCode })}`, function (value) {
                //     return Number(value) <= Number((`${calculatedPortfolioValue ?? ""}`?.replaceAll(',', '')))
                // })

            }))
        } else if (values?.transactionType !== "Withdrawal") {
            setValidations((prevState: Record<string, any>) => ({
                ...prevState,
                amount: yup
                    .string()
                    .transform((value) => `${value ?? ""}`?.replaceAll(',', ''))
                    .required('Amount is required')
                    .test('is-number', 'Amount should be a valid number', function (value) { return !Number.isNaN(Number(value)) })
                    .test('greater-than-zero', "Amount should be greater than 0", function (value) {
                        return Number(value) > 0
                    })
                // .test('whole-number', 'Amount should be a valid number', function (value) {
                //     return numberRegex.test(`${value ?? ""}`);
                // })
            }))
        }

        resetForm({
            values: {
                ...values,
                security: selectedObj,
                issuer: selectedObj?.issuerName ?? "",
                currentPortfolioValue: selectedObj ? calculatedPortfolioValue : "",
                lastValuationDate: dayjs(selectedObj?.marketPriceDate).isValid() ? dayjs(selectedObj?.marketPriceDate, "YYYYMMDD") : "",
                settlementBankNumber: selectedObj
                    ? {
                        bankCode: selectedObj?.bankCode ?? "",
                        bankAccCcy: selectedObj?.bankAccCcy ?? "",
                        bankAccType: selectedObj?.bankAccType ?? "",
                        bankAccNo: selectedObj?.bankAccNo ?? "",
                        bankAccCode: selectedObj?.bankAccCode ?? "",
                        bankName: selectedObj?.bankName ?? "",
                        label: selectedObj?.bankAccNo ?? "",
                        value: selectedObj?.bankAccNo ?? ""
                    }
                    : null,
                settlementBank: selectedObj?.bankName ?? "",
            }
        })
    }

    const handleTransactionTypeChange = async (event: SelectChangeEvent<unknown>) => {
        resetForm({
            values: {
                ...values,
                transactionType: event.target.value,
                security: null,
                issuer: "",
                amount: "",
                currentPortfolioValue: "",
                lastValuationDate: "",
                settlementBankNumber: null,
                settlementBank: "",
                settlementMethod: ""
            }
        })
    }

    const refactoredAmount = `${values?.amount ?? ""}`?.replaceAll(',', '')
    const refactoredCurrentPortfolioValue = `${values?.currentPortfolioValue ?? ""}`?.replaceAll(',', '')

    return (
        <Grid
            container
            columnSpacing={3}
        >
            {/* <Grid item xs={12} lg={4}>
                <MFTextField
                    name="orderNumber"
                    label="Order Number"
                    placeholder=""
                />
            </Grid> */}
            <Grid item xs={12} lg={4}>
                <TextDatePicker
                    name="orderDate"
                    label="Order Date *"
                    placeholder=""
                    maxDate={
                        businessDate ?
                            dayjs(businessDate, "DD/MM/YYYY").toDate() :
                            new Date()
                    }
                    customHandleChange={(selectedDate: any) => handleDateChange('orderDate', selectedDate)}
                    disabled={isLoading}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFSelectField
                    name="transactionType"
                    label="Transaction Type *"
                    items={[
                        { name: "Purchase" },
                        { name: "STP purchase" }, //call holding api
                        { name: "Withdrawal" } //call holding api
                    ].map((obj) => ({
                        key: obj.name,
                        value: obj.name,
                    }))}
                    disabled={isLoading || !values?.clientName}
                    onChange={handleTransactionTypeChange}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <FormikSearchDropdown
                    name="security"
                    label="Security *"
                    options={securitiesData?.securities?.map((obj: Record<string, any>) => ({
                        ...obj,
                        label: obj?.securityName ?? "",
                        value: obj?.security ?? ""
                    })) ?? []}
                    fetchData={fetchSecuritiesData}
                    disabled={isLoading || !values?.transactionType}
                    onChange={handleSecurityChange}
                    filterOptions={(x) => x}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="issuer"
                    label="Issuer"
                    placeholder=""
                    disabled={true}
                    value={values?.issuer ?? ""}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="amount"
                    label="Amount *"
                    placeholder=""
                    startAdornment={values?.amount ? (
                        <Typography
                            variant='body_500'
                            sx={(theme: Theme) => ({
                                color: (isLoading || !values?.security) ? theme.palette.text.secondary : theme.palette.text.primary
                            })}
                        >
                            {String.fromCharCode(parseInt(currencySymbol, 16))}
                        </Typography>
                    ) : undefined}
                    sx={{
                        '&.MuiOutlinedInput-root': {
                            "& fieldset>legend": {
                                fontSize: "0.78em",
                            }
                        }
                    }}
                    disabled={isLoading || !values?.security}
                    onKeyDown={allowDecimalNumbers}
                    formatCurrency
                    formatCurrencyLocaleOptions={{
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 4
                    }}
                    onPaste={handleAmountPaste}
                    value={values?.amount ?? ""}
                />
                <CurrencyConversionToWords value={refactoredAmount} />
            </Grid>
            {values?.transactionType === "Withdrawal" && (
                <>
                    <Grid item xs={12} lg={4}>
                        <MFTextField
                            name="currentPortfolioValue"
                            label="Current Portfolio Value"
                            placeholder=""
                            startAdornment={values?.currentPortfolioValue ? (
                                <Typography
                                    variant='body_500'
                                    sx={(theme: Theme) => ({ color: theme.palette.text.secondary })}
                                >
                                    {String.fromCharCode(parseInt(currencySymbol, 16))}
                                </Typography>
                            ) : undefined}
                            sx={{
                                '&.MuiOutlinedInput-root': {
                                    "& fieldset>legend": {
                                        fontSize: "0.78em",
                                    }
                                }
                            }}
                            disabled={true}
                            formatCurrency
                            value={values?.currentPortfolioValue ?? ""}
                            onKeyDown={allowDecimalNumbers}
                        />
                        <CurrencyConversionToWords value={refactoredCurrentPortfolioValue} />
                    </Grid>
                    <Grid item xs={12} lg={4}>
                        <TextDatePicker
                            name="lastValuationDate"
                            label="Last Valuation Date"
                            placeholder=""
                            disabled={true}
                            maxDate={new Date("2099-12-31")}
                            customHandleChange={(selectedDate: any) => handleDateChange('lastValuationDate', selectedDate)}
                        />
                    </Grid>
                </>
            )}
            <Grid item xs={12}>
                <MFTextField
                    name="remarks"
                    label="Remarks *"
                    placeholder=""
                    multiline
                    inputProps={{ maxLength: 200 }}
                    value={values?.remarks ?? ""}
                    disabled={isLoading}
                />
                <Typography
                    variant="overline_400"
                    sx={(theme: Theme) => ({
                        textAlign: "end",
                        color: theme.palette.text.secondary,
                        marginTop: "-10px"
                    })}
                >
                    Max 200 characters
                </Typography>
            </Grid>
        </Grid>
    )
}